import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Form, Input, Select, Switch, Typography, message, Alert } from 'antd';
import Swal from 'sweetalert2';
import tenantService from '../../../services/tenantService';
import { buildTenantLoginUrl, getAppContext } from '../../../config/appContext';

const formatPlanPrice = (price) => {
  const amount = Number(price);
  if (!amount) return 'S/ 0';
  return `S/ ${amount.toFixed(2)}/mes`;
};

const formatStorage = (mb) => {
  if (mb >= 1024) {
    const gb = mb / 1024;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }
  return `${mb} MB`;
};

const planOptionLabel = (plan) => {
  const internal = plan.slug === 'starter' ? ' (interno)' : '';
  const buildings = plan.max_inmuebles === 1 ? '1 edificio' : `${plan.max_inmuebles} edificios`;
  return `${plan.name}${internal} — ${formatPlanPrice(plan.price_monthly)} · ${buildings} · ${plan.max_usuarios} usuarios`;
};

const planDescription = (plan) => {
  if (!plan) return null;

  const buildings =
    plan.max_inmuebles === 1
      ? '1 edificio en la cuenta'
      : `Hasta ${plan.max_inmuebles} edificios en la cuenta`;

  const lines = [
    buildings,
    'Pisos, departamentos y locales sin límite por edificio',
    `Hasta ${plan.max_usuarios} usuarios`,
    `${formatStorage(plan.max_storage_mb)} para documentos`,
  ];

  if (plan.slug === 'starter') {
    lines.push('Solo para demos internas; no se muestra en la web pública.');
  }
  if (plan.slug === 'profesional') {
    lines.push('En la web se ofrece 1 mes gratis con 1 edificio de prueba.');
  }

  return lines;
};

const showTenantCreatedAlert = async (tenant, navigate) => {
  const loginUrl = buildTenantLoginUrl(tenant.slug);

  await Swal.fire({
    title: '¡Cliente creado!',
    icon: 'success',
    html: `
      <div style="text-align:left;font-size:14px;line-height:1.7;color:#334155">
        <p style="margin:0 0 8px"><strong>Empresa:</strong> ${tenant.company_name}</p>
        <p style="margin:0 0 8px"><strong>Subdominio:</strong> ${tenant.domain || tenant.slug}</p>
        <p style="margin:0 0 8px">
          <strong>URL de login:</strong><br/>
          <a href="${loginUrl}" target="_blank" rel="noreferrer" style="color:#2E37A4;word-break:break-all">${loginUrl}</a>
        </p>
        <p style="margin:0"><strong>Admin:</strong> ${tenant.admin_username} / ${tenant.admin_password}</p>
      </div>
    `,
    confirmButtonText: 'Ver listado de clientes',
    confirmButtonColor: '#2E37A4',
    allowOutsideClick: false,
    customClass: {
      popup: 'rentahab-swal-popup',
      confirmButton: 'rentahab-swal-confirm',
    },
  });

  navigate('/tenants');
};

const TenantCreate = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const domainSuffix = getAppContext().tenantDomainSuffix;
  const selectedPlanSlug = Form.useWatch('plan', form);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.slug === selectedPlanSlug),
    [plans, selectedPlanSlug],
  );

  useEffect(() => {
    tenantService.listarPlanes().then(setPlans).catch(() => {});
  }, []);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const data = await tenantService.crear(values);
      form.resetFields();
      await showTenantCreatedAlert(data.tenant, navigate);
    } catch (error) {
      const msg = error.response?.data?.message || 'Error al crear el cliente';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Nuevo cliente">
      <Typography.Paragraph type="secondary">
        Se creará automáticamente la base de datos, migraciones, admin inicial y subdominio.
        El plan limita cuántos <strong>edificios</strong> puede registrar el cliente, no cuántos
        pisos o espacios tiene cada uno.
      </Typography.Paragraph>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          plan: 'profesional',
          admin_username: 'admin',
          country: 'Perú',
          show_on_map: true,
        }}
      >
        <Form.Item label="Nombre de la empresa" name="company_name" rules={[{ required: true }]}>
          <Input placeholder="Inmobiliaria ACME" />
        </Form.Item>
        <Form.Item
          label="Subdominio (slug)"
          name="slug"
          rules={[{ required: true, pattern: /^[a-z0-9-]+$/, message: 'Solo minúsculas, números y guiones' }]}
        >
          <Input placeholder="acme" addonAfter={domainSuffix} />
        </Form.Item>
        <Form.Item label="Ciudad" name="city" rules={[{ required: true, message: 'Indique la ciudad para el mapa de clientes' }]}>
          <Input placeholder="Lima" />
        </Form.Item>
        <Form.Item label="País" name="country">
          <Input placeholder="Perú" />
        </Form.Item>
        <Form.Item
          label="Plan"
          name="plan"
          rules={[{ required: true, message: 'Seleccione un plan' }]}
          extra="Starter es solo para pruebas internas. Clientes reales suelen ir en Profesional o Enterprise."
        >
          <Select
            options={plans.map((p) => ({ value: p.slug, label: planOptionLabel(p) }))}
          />
        </Form.Item>
        {selectedPlan && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Límites del plan ${selectedPlan.name}`}
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {planDescription(selectedPlan).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            }
          />
        )}
        <Form.Item label="Mostrar en mapa público" name="show_on_map" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item label="Usuario admin del cliente" name="admin_username" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Contraseña admin" name="admin_password" rules={[{ required: true, min: 8 }]}>
          <Input.Password />
        </Form.Item>
        <Form.Item label="Email admin" name="admin_email" rules={[{ type: 'email' }]}>
          <Input placeholder="admin@acme.rentahab.com" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          Crear y provisionar
        </Button>
        <Button style={{ marginLeft: 8 }} onClick={() => navigate('/tenants')}>
          Volver
        </Button>
      </Form>
    </Card>
  );
};

export default TenantCreate;
